import { InfoItem } from '@src/components/organisms/numa-info/types';
import { BreakfastPartner } from '@src/features/breakfast/types';
import { Partner } from '@src/lib/ssr/types';
import { ExtraServiceWithTimeSlice } from '@src/types/checkout';

export type DeepLinkValue = { param: string; value: string };
/** A deep link whose value comes from its argument; `param` names its query param without one. */
export type DeepLinkFunction<T> = ((arg: T) => DeepLinkValue) & { param: string };

function dynamicDeepLink<T>(param: string, toValue: (arg: T) => string): DeepLinkFunction<T> {
  return Object.assign((arg: T) => ({ param, value: toValue(arg) }), { param });
}

// dynamic functional deep links

const infoItem = dynamicDeepLink<InfoItem['type']['slug']>('info-item', (slug) => slug);
const partner = dynamicDeepLink<Partner['partner']>('partner', ({ partnerCode }) => partnerCode);
const extraService = dynamicDeepLink<Pick<ExtraServiceWithTimeSlice, 'code'>>('extra-service', ({ code }) => code);
const memberBenefit = dynamicDeepLink<string>('member-benefit', (benefitType) => benefitType);
const bookedService = dynamicDeepLink<Pick<ExtraServiceWithTimeSlice, 'code'>>('booked-service', ({ code }) => code);
// TODO: this should be a slug, the ID should not be exposed ideally
const breakfastPartner = dynamicDeepLink<BreakfastPartner>('breakfast-partner', (partner) => partner.id);

export const UNIT_GROUP_ID_QUERY_PARAM = 'unitGroupId';

const roomUpgradeUnitGroup = dynamicDeepLink<string>(UNIT_GROUP_ID_QUERY_PARAM, (unitGroupId) => unitGroupId);

/**
 * The Pages Router's URL-addressable UI: sections, modals and features a link or the back button can
 * open. Each holds client state only, so a back that changes nothing but these params never reloads
 * the page's server data (see `useDeepLinkBackNavigation`). A new modal opened from the URL registers
 * its param here and opens with `useQueryModalState`.
 */
export const deepLinks = {
  // sections
  roomInfo: {
    param: 'section',
    value: 'room-info',
  },
  bookingDetails: {
    param: 'section',
    value: 'booking-details',
  },
  extraServices: {
    param: 'section',
    value: 'extras',
  },
  infoItems: {
    param: 'section',
    value: 'numa-info',
  },
  location: {
    param: 'section',
    value: 'location',
  },
  breakfastMenus: {
    param: 'section',
    value: 'breakfast-menus',
  },

  // sub-sections
  roomGallery: {
    param: 'sub-section',
    value: 'room-images',
  },
  roomGuide: {
    param: 'sub-section',
    value: 'room-guide',
  },
  roomAmenities: {
    param: 'sub-section',
    value: 'room-amenities',
  },

  // features
  checkout: {
    param: 'feature',
    value: 'checkout',
  },
  cancelTrip: {
    param: 'feature',
    value: 'cancel',
  },
  deactivateAccount: {
    param: 'feature',
    value: 'deactivate-account',
  },
  linkTrip: {
    param: 'feature',
    value: 'link-trip',
  },
  extendStay: {
    param: 'feature',
    value: 'extend-stay',
  },
  roomUpgradeUpsell: {
    param: 'feature',
    value: 'room-upgrade-upsell',
  },
  amendDates: {
    param: 'feature',
    value: 'amend-dates',
  },
  amendGuests: {
    param: 'feature',
    value: 'amend-guests',
  },
  requestDifferentTimes: {
    param: 'feature',
    value: 'checkinout-request',
  },
  travelGuide: {
    param: 'feature',
    value: 'travel-guide',
  },
  breakfastSelection: {
    param: 'feature',
    value: 'select-breakfast',
  },

  // dynamic functional deep links
  infoItem,
  partner,
  extraService,
  memberBenefit,
  bookedService,
  breakfastPartner,
  roomUpgradeUnitGroup,
};

/** Every param in `deepLinks`. */
export const DEEP_LINK_PARAMS: ReadonlySet<string> = new Set(Object.values(deepLinks).map(({ param }) => param));

type DeepLinks = typeof deepLinks;
export type DeepLinkType = keyof DeepLinks;
export type DeepLinkArgs<T extends DeepLinkType> =
  DeepLinks[T] extends DeepLinkFunction<infer Args> ? { type: T; value: Args } : { type: T };

type UnknownDeepLink<T extends DeepLinkType> = DeepLinks[T] | DeepLinkFunction<unknown> | DeepLinkValue;

function isDeepLinkFunction<T extends DeepLinkType>(
  deepLink: UnknownDeepLink<T>,
): deepLink is DeepLinkFunction<unknown> {
  return typeof deepLink === 'function';
}

function isDeepLinkValue<T extends DeepLinkType>(deepLink: UnknownDeepLink<T>): deepLink is DeepLinkValue {
  return typeof deepLink === 'object';
}

export function getDeepLink<T extends DeepLinkType>(args: DeepLinkArgs<T>): DeepLinkValue {
  const deepLink = deepLinks[args.type];

  if (isDeepLinkFunction(deepLink) && 'value' in args) {
    return deepLink(args.value as any);
  }

  if (isDeepLinkValue(deepLink)) {
    return deepLink;
  }

  throw new Error(`Invalid deep link key: ${args.type}`);
}
